import { create } from 'zustand';
import type { PlatoonPhase } from '../types/domain';

// Trimmed platoon descriptor carried on realtime payloads (no PII).
// Mirrors the `platoon` object in the API contract §12 socket payloads.
export interface PlatoonRef {
  id: string;
  callsign: string;
  platoonNumber?: number;
  unitId?: string;
  farmbaseId?: string | null;
  currentPhase?: PlatoonPhase;
  commanderName?: string | null;
  strength?: number | null;
  sos?: boolean;
  unit?: {
    id: string;
    name: string;
    code: string;
  };
}

export interface Location {
  deviceId: string;
  platoonId?: string;
  unitId?: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
  speed?: number;
  bearing?: number;
  battery?: number;
  signal?: number;
  deviceTime: string;
  serverTime: string;
  platoon?: PlatoonRef; // top-level (contract location:update payload)
  device?: {
    id: string;
    model?: string;
    platoon?: PlatoonRef; // some payloads nest the platoon under the device
  };
}

export interface LocationHistory {
  deviceId: string;
  points: Array<{
    latitude: number;
    longitude: number;
    timestamp: string;
  }>;
}

export interface SOSEvent {
  id: string;
  platoonId?: string;
  unitId?: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
  triggerType: 'BUTTON' | 'SHAKE' | 'POWER_KEY' | 'VOLUME_KEYS' | 'AUTOMATIC';
  status: 'TRIGGERED' | 'ACKNOWLEDGED' | 'RESPONDING' | 'RESOLVED' | 'CANCELLED';
  triggeredAt: string;
  acknowledgedAt?: string;
  resolvedAt?: string;
  notes?: string;
  platoon?: PlatoonRef;
  acknowledgedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  responders?: Array<{
    id: string;
    responderId: string;
    responderType: string;
    assignedAt: string;
    enRouteAt?: string;
    arrivedAt?: string;
    estimatedEta?: string;
  }>;
}

export interface Alert {
  id: string;
  type: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
  status: 'ACTIVE' | 'ACKNOWLEDGED' | 'RESOLVED' | 'EXPIRED';
  title: string;
  message: string;
  platoonId?: string;
  deviceId?: string;
  unitId?: string;
  createdAt: string;
  acknowledgedAt?: string;
  metadata?: Record<string, any>;
}

export interface Notification {
  id: string;
  type: 'USER_MESSAGE' | 'ALERT' | 'SOS' | 'SYSTEM' | 'TRACKING_ALERT';
  title: string;
  message: string;
  senderName?: string;
  senderEmail?: string;
  timestamp: Date;
  read: boolean;
}

interface RealtimeState {
  // Connection status
  isConnected: boolean;
  setConnected: (connected: boolean) => void;
  
  // Live locations
  locations: Map<string, Location>;
  updateLocation: (location: Location) => void;
  removeLocation: (deviceId: string) => void;
  setLocations: (locations: Location[]) => void;
  
  // Location history for trails
  locationHistory: Map<string, LocationHistory>;
  addToLocationHistory: (deviceId: string, point: { latitude: number; longitude: number; timestamp: string }) => void;
  setLocationHistory: (deviceId: string, history: LocationHistory) => void;
  clearLocationHistory: (deviceId: string) => void;
  
  // SOS Events
  sosEvents: SOSEvent[];
  addSOSEvent: (event: SOSEvent) => void;
  updateSOSEvent: (id: string, updates: Partial<SOSEvent>) => void;
  removeSOSEvent: (id: string) => void;
  setSOSEvents: (events: SOSEvent[]) => void;
  
  // Alerts
  alerts: Alert[];
  addAlert: (alert: Alert) => void;
  updateAlert: (id: string, updates: Partial<Alert>) => void;
  removeAlert: (id: string) => void;
  setAlerts: (alerts: Alert[]) => void;
  
  // Notifications (messages from app)
  notifications: Notification[];
  addNotification: (notification: Notification) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
  latestNotification: Notification | null;
  clearLatestNotification: () => void;
  
  // Stats
  stats: {
    activeDevices: number;
    activeSOS: number;
    activeAlerts: number;
  };
  setStats: (stats: { activeDevices: number; activeSOS: number; activeAlerts: number }) => void;
  
  // Selected items for details view
  selectedLocation: string | null;
  selectLocation: (deviceId: string | null) => void;
  
  selectedSOS: string | null;
  selectSOS: (sosId: string | null) => void;
}

export const useRealtimeStore = create<RealtimeState>((set, _get) => ({
  isConnected: false,
  setConnected: (connected) => set({ isConnected: connected }),
  
  // Locations
  locations: new Map(),
  updateLocation: (location) => set((state) => {
    const newLocations = new Map(state.locations);
    newLocations.set(location.deviceId, location);
    
    // Also add to location history for trails
    const history = state.locationHistory.get(location.deviceId) || {
      deviceId: location.deviceId,
      points: [],
    };
    
    // Add new point to history (keep last 100 points)
    history.points = [
      ...history.points.slice(-99),
      {
        latitude: location.latitude,
        longitude: location.longitude,
        timestamp: location.serverTime,
      },
    ];
    
    const newHistory = new Map(state.locationHistory);
    newHistory.set(location.deviceId, history);
    
    // Update stats to reflect new active device count
    const newStats = {
      ...state.stats,
      activeDevices: newLocations.size,
    };
    
    return { 
      locations: newLocations,
      locationHistory: newHistory,
      stats: newStats,
    };
  }),
  removeLocation: (deviceId) => set((state) => {
    const newLocations = new Map(state.locations);
    newLocations.delete(deviceId);
    
    // Update stats to reflect removed device
    const newStats = {
      ...state.stats,
      activeDevices: newLocations.size,
    };
    
    return { 
      locations: newLocations,
      stats: newStats,
    };
  }),
  setLocations: (locations) => set((state) => {
    const locationsMap = new Map(locations.map(loc => [loc.deviceId, loc]));
    
    // Update stats when setting all locations
    const newStats = {
      ...state.stats,
      activeDevices: locationsMap.size,
    };
    
    return {
      locations: locationsMap,
      stats: newStats,
    };
  }),
  
  // Location History
  locationHistory: new Map(),
  addToLocationHistory: (deviceId, point) => set((state) => {
    const history = state.locationHistory.get(deviceId) || {
      deviceId,
      points: [],
    };
    
    history.points = [...history.points.slice(-99), point];
    
    const newHistory = new Map(state.locationHistory);
    newHistory.set(deviceId, history);
    
    return { locationHistory: newHistory };
  }),
  setLocationHistory: (deviceId, history) => set((state) => {
    const newHistory = new Map(state.locationHistory);
    newHistory.set(deviceId, history);
    return { locationHistory: newHistory };
  }),
  clearLocationHistory: (deviceId) => set((state) => {
    const newHistory = new Map(state.locationHistory);
    newHistory.delete(deviceId);
    return { locationHistory: newHistory };
  }),
  
  // SOS Events
  sosEvents: [],
  addSOSEvent: (event) =>
    set((state) => {
      // Defensive: never store invalid events (prevents UI crashes)
      if (!event || typeof (event as any).id !== 'string' || !(event as any).id) {
        return state;
      }
      const existing = (state.sosEvents || []).filter(Boolean) as any[];
      return {
        sosEvents: [event, ...existing.filter((e) => e && e.id !== (event as any).id)],
      };
    }),
  updateSOSEvent: (id, updates) =>
    set((state) => ({
      sosEvents: (state.sosEvents || [])
        .filter(Boolean)
        .map((e: any) => (e && e.id === id ? { ...e, ...updates } : e)),
    })),
  removeSOSEvent: (id) =>
    set((state) => ({
      sosEvents: (state.sosEvents || []).filter((e: any) => e && e.id !== id),
    })),
  setSOSEvents: (events) =>
    set({
      sosEvents: Array.isArray(events)
        ? (events.filter((e: any) => e && typeof e.id === 'string' && e.status) as any)
        : [],
    }),
  
  // Alerts
  alerts: [],
  addAlert: (alert) => set((state) => {
    if (!alert || !alert.id) {
      console.warn('addAlert: invalid alert', alert);
      return state;
    }
    return {
      alerts: [alert, ...state.alerts.filter(a => a.id !== alert.id)],
    };
  }),
  updateAlert: (id, updates) => set((state) => ({
    alerts: state.alerts.map(a => 
      a.id === id ? { ...a, ...updates } : a
    ),
  })),
  removeAlert: (id) => set((state) => ({
    alerts: state.alerts.filter(a => a.id !== id),
  })),
  setAlerts: (alerts) => set({ alerts }),
  
  // Notifications
  notifications: [],
  addNotification: (notification) => set((state) => ({
    notifications: [notification, ...state.notifications].slice(0, 50), // Keep last 50
    latestNotification: notification,
  })),
  markNotificationRead: (id) => set((state) => ({
    notifications: state.notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    ),
  })),
  clearNotifications: () => set({ notifications: [] }),
  latestNotification: null,
  clearLatestNotification: () => set({ latestNotification: null }),
  
  // Stats
  stats: {
    activeDevices: 0,
    activeSOS: 0,
    activeAlerts: 0,
  },
  setStats: (stats) => set({ stats }),
  
  // Selection
  selectedLocation: null,
  selectLocation: (deviceId) => set({ selectedLocation: deviceId }),
  
  selectedSOS: null,
  selectSOS: (sosId) => set({ selectedSOS: sosId }),
}));
